import { useState } from 'react';
import { ArrowInline } from "@/lib/Icon";
import { Link, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ExternalLink, X } from 'lucide-react';

import {
  useCancelOrder,
  useClinicalState,
  useOrder,
  useMySubscriptions,
} from '@/lib/api/hooks';
import { Button } from '@/design-system/primitives';
import { track } from '@/lib/analytics';
import { cn } from '@/lib/cn';
import { PageHeader } from '@/components/app-shell';
import { supportChat } from '@/components/app-shell/support/supportChatUi';
import type { CancelOrderResponse } from '@/types/api';

export function cancellationSummary(result: CancelOrderResponse): string {
  if (result.cancellation_outcome === 'confirmed' && result.status === 'cancelled') {
    const subscription = result.subscription_cancelled
      ? ' Your subscription has been stopped.' : '';
    const followups = result.followups_pending
      ? ' Related account updates are still pending.' : '';
    return `Your order has been cancelled.${subscription}${followups}`;
  }
  if (result.cancellation_outcome === 'pending') {
    return 'Your cancellation request has been submitted. Pharmacy confirmation is pending.';
  }
  if (result.cancellation_outcome === 'refused') {
    return 'The pharmacy could not cancel this order. Please contact support for help.';
  }
  return 'Cancellation has not been confirmed. Please contact support for help.';
}

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}

const STATUS_LABEL: Record<string, string> = {
  awaiting_prescription: 'AWAITING RX',
  awaiting_pharmacy_routing: 'AWAITING ROUTING',
  pending_review: 'PENDING REVIEW',
  pending_approval: 'PENDING APPROVAL',
  processing: 'PROCESSING',
  shipped: 'SHIPPED',
  delivered: 'DELIVERED',
  cancelled: 'CANCELLED',
  failed: 'FAILED',
};

function statusLabel(status: string, awaitingConsult: boolean): string {
  if (status === 'awaiting_prescription' && awaitingConsult) {
    return 'AWAITING CONSULT';
  }
  return STATUS_LABEL[status] ?? status.replace(/_/g, ' ').toUpperCase();
}

/**
 * OrderDetail — mock-matched.
 *
 * Layout: order number title + ▶ subtitle + ← BACK TO ORDERS link.
 * ITEMS card lists each line with right-aligned subtotal, footer row
 * is TOTAL. SHIPPING card renders a vertical timeline with a sunken
 * left rail. Bottom: black Track Package CTA + sunken Request Refill
 * row. Cancel flow preserved (collapses into a tiny confirm).
 */
export function OrderDetail() {
  const { orderId } = useParams();
  const { data: order, isLoading, error } = useOrder(orderId);
  const { data: clinical } = useClinicalState();
  const needsSyncConsult = Boolean(clinical?.needs_sync_consult);
  const awaitingConsult = Boolean(clinical?.awaiting_consult);
  const cancel = useCancelOrder();
  // Cross-reference stripe_subscription_id → MeSubscription.plan_cycles
  // to surface a Monthly/Quarterly badge. /me/subscriptions is cached
  // app-wide so this is free after first paint.
  const { data: subsData } = useMySubscriptions();
  const matchedSub = order?.stripe_subscription_id
    ? subsData?.subscriptions.find(
        (s) => s.stripe_subscription_id === order.stripe_subscription_id,
      )
    : null;
  const cadenceLabel = matchedSub
    ? matchedSub.plan_cycles === 3
      ? 'QUARTERLY'
      : matchedSub.plan_cycles === 1
        ? 'MONTHLY'
        : `EVERY ${matchedSub.plan_cycles} MONTHS`
    : null;
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancellationResult, setCancellationResult] = useState<CancelOrderResponse | null>(null);
  // A later order refresh can settle the pharmacy request. It proves only
  // order cancellation, never subscription, refund, or other follow-up work.
  const pharmacyPendingSettled = order?.status === 'cancelled'
    && (cancellationResult?.cancellation_outcome === 'pending'
      || cancellationResult?.cancellation_outcome === 'uncertain'
      || cancellationResult?.cancellation_outcome === undefined);
  const currentCancellation = cancellationResult?.id === order?.id && !pharmacyPendingSettled
    ? cancellationResult : null;

  if (isLoading) {
    return <div className="h-[40rem] bg-[var(--surface-sunken)] animate-pulse" />;
  }
  if (error || !order) {
    return (
      <div className="max-w-form">
        <Link
          to="/app/orders"
          className="inline-flex items-center gap-5 type-label-sm text-ink hover:text-ink-secondary"
        >
          <span aria-hidden>←</span> BACK TO ORDERS
        </Link>
        <div className="mt-20 type-body-md text-ink-secondary">
          Couldn&apos;t load this order.
        </div>
      </div>
    );
  }

  const created = parseISO(order.created_at);
  const carrier = order.shipment?.carrier?.toUpperCase();
  const shippedAt = order.shipment?.shipped_at
    ? parseISO(order.shipment.shipped_at)
    : null;
  const delivered = order.shipment?.delivered_at
    ? parseISO(order.shipment.delivered_at)
    : null;

  return (
    <div className="flex flex-col">
      <PageHeader
        title={order.order_number}
        description={`This order was placed on ${format(created, 'MMMM d, yyyy')}.`}
      />
      <div className="flex flex-col px-15 md:px-40 py-25 gap-25 max-w-[65rem] mx-auto w-full">

      <div className="flex items-center justify-between gap-10">
        <Link
          to="/app/orders"
          className="inline-flex items-center underline gap-8 type-label-sm text-ink-muted hover:text-ink self-start"
        >
          {/* <span aria-hidden className="inline-flex items-center justify-center w-10 h-12 rotate-180">
            <Icon name="Arrow" viewBox="0 0 10 12" />
          </span> */}
          Back to orders
        </Link>
  
        {/* Status pill row (only when not the basic 'placed' state — the
            header already says when the order was placed). */}
        {(order.status !== 'pending_review' || cadenceLabel) && (
          <div className="flex items-center gap-8 flex-wrap">
            {order.status !== 'pending_review' && (
              <span className="type-label-xs text-ink-secondary bg-[var(--surface-sunken)] px-10 py-5">
                {statusLabel(order.status, awaitingConsult)}
              </span>
            )}
            {cadenceLabel && (
              <span className="type-label-xs bg-ink text-white px-10 py-5">
                {cadenceLabel}
              </span>
            )}

          </div>
        )}
        {order.issue && (
          <div className="type-body-sm text-warning mt-10">
            {order.issue}{' '}
            {!order.fulfillment_conflict && /address/i.test(order.issue) ? (
              <Link to="/app/account" className="underline underline-offset-4 font-medium">
                Update address <ArrowInline />
              </Link>
            ) : (
              <button type="button" onClick={() => supportChat.open()}
                className="underline underline-offset-4 font-medium">
                Contact support <ArrowInline />
              </button>
            )}
          </div>
        )}
      </div>

      {order.status === 'awaiting_prescription' && awaitingConsult && (
        <section className="border border-line-subtle bg-[var(--surface-sunken)] px-20 py-15">
          <p className="type-label-sm text-ink-secondary">
            AWAITING CONSULT
          </p>
          <p className="type-body-md text-ink mt-8">
            {needsSyncConsult ? (
              <>
                Your state requires a live visit with a clinician before
                your prescription can be written. Book a time on the{' '}
                <Link
                  to="/app/clinic?tab=appointments"
                  className="underline underline-offset-4"
                >
                  Clinic
                </Link>{' '}
                tab — your script is written after the visit. Cancelling
                or rescheduling resets the wait.
              </>
            ) : (
              <>
                Your visit is on the calendar. Your clinician will write
                your prescription after the appointment — manage it from
                the{' '}
                <Link
                  to="/app/clinic?tab=appointments"
                  className="underline underline-offset-4"
                >
                  Clinic
                </Link>{' '}
                tab. Cancelling resets the wait.
              </>
            )}
          </p>
        </section>
      )}

      {/* ITEMS */}
      <section className="border border-line-subtle bg-[var(--surface-raised)]">
        <div className="px-15 py-12 bg-[var(--surface-raised)] border-b border-line-subtle">
          <span className="type-label-sm text-ink-secondary">ITEMS</span>
        </div>
        <ul className="flex flex-col">
          {order.lines.map((l, i) => (
            <li
              key={i}
              className="flex items-baseline justify-between gap-20 px-15 py-15 border-b border-line-subtle last:border-b-0"
            >
              <span className="type-label-md truncate">{l.name}</span>
              <span className="type-data-md shrink-0">
                {dollars(l.unit_price_cents * l.quantity)}
              </span>
            </li>
          ))}
        </ul>
        <div className="flex items-baseline justify-between px-15 py-15 border-t border-line-subtle">
          <span className="type-label-sm text-ink-secondary">ORDER TOTAL</span>
          <span className="type-label-md">{dollars(order.total_cents)}</span>
        </div>
        {order.amount_paid_cents != null && (
          <div className="px-15 py-15 border-t border-line-subtle bg-[var(--surface-sunken)]">
            <div className="flex items-baseline justify-between gap-15">
              <span className="type-label-sm text-ink-secondary">INVOICE / CHARGE PAID</span>
              <span className="type-label-md">{dollars(order.amount_paid_cents)}</span>
            </div>
            <p className="type-body-sm text-ink-muted mt-8">
              This payment may cover multiple items or scheduled shipments.
            </p>
          </div>
        )}
      </section>

      {/* SHIPPING timeline */}
      <section className="border border-line-subtle bg-[var(--surface-raised)]">
        <div className="px-15 py-12 bg-[var(--surface-raised)] border-b border-line-subtle">
          <span className="type-label-sm text-ink-secondary">SHIPPING</span>
        </div>
        <ShippingTimeline
          orderCreatedAt={created}
          shippedAt={shippedAt}
          deliveredAt={delivered}
          carrier={carrier}
          status={order.status}
        />
      </section>

      {/* CTAs */}
      <div className="flex flex-col gap-10">
        {order.shipment?.tracking_url ? (
          <Button asChild className="w-full justify-between h-50 px-15">
            <a
              href={order.shipment.tracking_url}
              target="_blank"
              rel="noreferrer"
            >
              Track Package
              <ExternalLink size={14} strokeWidth={1.5} />
            </a>
          </Button>
        ) : (
          <Button disabled className="w-full justify-between h-50 px-15">
            Track Package
            <ExternalLink size={14} strokeWidth={1.5} />
          </Button>
        )}
        {/* Request Refill: intentionally hidden — refill flow is automatic
            (subscription cycles + invoice.paid webhook re-fires fulfillment).
            See task #199 / Orders comment policy. */}
        {order.cancellable && !showCancelConfirm && (
          <button
            type="button"
            onClick={() => setShowCancelConfirm(true)}
            className="inline-flex items-center gap-8 self-start type-label-sm text-ink-muted hover:text-ink mt-5"
          >
            <X size={12} strokeWidth={1.5} /> Cancel order
          </button>
        )}
        {showCancelConfirm && (
          <div className="border border-line-subtle px-15 py-15">
            <p className="type-body-sm">
              {order.is_subscription
                ? 'Cancel this fill AND stop the subscription?'
                : 'Cancel this order?'}
            </p>
            <div className="flex items-center gap-10 mt-15">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancel.isPending}
              >
                Keep
              </Button>
              <Button
                variant="danger"
                size="sm"
                loading={cancel.isPending}
                onClick={() =>
                  cancel
                    .mutateAsync(order.id)
                    .then((result) => {
                      setCancellationResult(result);
                      if (result.id === order.id && result.cancellation_outcome === 'confirmed'
                          && result.status === 'cancelled') {
                        track('order_cancelled', { order_id: order.id });
                      }
                      setShowCancelConfirm(false);
                    })
                    .catch(() => {})
                }
              >
                Yes, cancel
              </Button>
            </div>
            {cancel.error && (
              <div className="type-body-sm text-danger mt-10">
                {(cancel.error as { message?: string }).message ??
                  'Cancel failed'}
              </div>
            )}
          </div>
        )}
        {currentCancellation && (
          <div role="status" className="type-body-sm text-ink-muted">
            {cancellationSummary(currentCancellation)}
          </div>
        )}
        {!currentCancellation && order.status === 'cancelled' && (
          <div className="type-body-sm text-danger">
            This order has been cancelled.
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

function ShippingTimeline({
  orderCreatedAt,
  shippedAt,
  deliveredAt,
  carrier,
  status,
}: {
  orderCreatedAt: Date;
  shippedAt: Date | null;
  deliveredAt: Date | null;
  carrier?: string;
  status: string;
}) {
  type Step = { label: string; date: string | null; done: boolean };
  const isCancelled = status === 'cancelled' || status === 'failed';
  // The backend projects shipment evidence into status for every surface.
  // Timestamps and tracking labels must not independently advance this timeline.
  const isDelivered = status === 'delivered';
  const isShipped = isDelivered || status === 'shipped';
  const isProcessing =
    isShipped || status === 'processing' || status === 'pending_review';

  const steps: Step[] = [
    {
      label: 'Order placed',
      date: format(orderCreatedAt, 'MMM d, yyyy'),
      done: true,
    },
    {
      label: carrier ? `Shipped via ${carrier}` : 'Shipped',
      date: isShipped && shippedAt ? format(shippedAt, 'MMM d, yyyy') : isProcessing && !isShipped ? 'Awaiting carrier handoff' : null,
      done: isShipped,
    },
    {
      label: 'Delivered',
      date: isDelivered && deliveredAt ? format(deliveredAt, 'MMM d, yyyy') : null,
      done: isDelivered,
    },
  ];
  if (isCancelled) {
    steps.splice(1, 2, { label: status === 'failed' ? 'Failed' : 'Cancelled', date: null, done: true });
  }

  return (
    <ol className="flex flex-col">
      {steps.map((s, i) => (
        <li
          key={i}
          className={cn(
            'flex items-stretch',
            i < steps.length - 1 && 'border-b border-line-subtle',
          )}
        >
          {/* Icon cell — flex item with its own background so we can
              color it per-row (done vs pending). */}
          <div
            aria-hidden
            className={cn(
              'w-60 min-h-60 shrink-0 flex items-center justify-center border-r border-line-subtle',
              s.done
                ? 'bg-accent text-white'
                : 'bg-[var(--surface-sunken)] text-ink-muted',
            )}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round">
              <path d="M3 8L12 3l9 5v8l-9 5-9-5V8z" />
              <path d="M3 8l9 5 9-5" />
              <path d="M12 13v9" />
            </svg>
          </div>
          <div className="flex-1 min-w-0 px-15 py-15 flex flex-col gap-5 justify-center">
            <div className="type-label-sm text-ink leading-tight">
              {s.label.toUpperCase()}
            </div>
            {s.date && (
              <div className="type-data-sm text-ink-muted">{s.date}</div>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}
